import { ChevronDown } from "lucide-react";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { cx, focusRing } from "@/lib/utils";

export type ArbeitspaketAenderung =
  | {
      art: "anlegen";
      pfad: string;
      inhalt: string;
    }
  | {
      art: "aendern";
      pfad: string;
      ersetzungen: Array<{
        vorher: string;
        nachher: string;
      }>;
    };

export function hatVollstaendigePatchVorschau(
  aenderungen: ArbeitspaketAenderung[],
  angewendeteDateipfade: string[],
): boolean {
  if (aenderungen.length === 0 || angewendeteDateipfade.length === 0) {
    return false;
  }
  const patchPfade = new Set(aenderungen.map((aenderung) => aenderung.pfad));
  const dateiPfade = new Set(angewendeteDateipfade);
  if (
    patchPfade.size !== aenderungen.length ||
    dateiPfade.size !== angewendeteDateipfade.length ||
    patchPfade.size !== dateiPfade.size ||
    ![...patchPfade].every((pfad) => pfad.length > 0 && dateiPfade.has(pfad))
  ) {
    return false;
  }
  return aenderungen.every((aenderung) => {
    if (aenderung.art === "anlegen") return typeof aenderung.inhalt === "string";
    return (
      aenderung.ersetzungen.length > 0 &&
      aenderung.ersetzungen.every(
        (ersetzung) =>
          typeof ersetzung.vorher === "string" &&
          typeof ersetzung.nachher === "string",
      )
    );
  });
}

function CodeAusschnitt({
  titel,
  inhalt,
  art,
}: {
  titel: string;
  inhalt: string;
  art: "vorher" | "nachher" | "neu";
}) {
  const istVorher = art === "vorher";
  return (
    <div
      className={cx(
        "min-w-0 border-l-2 bg-papier",
        istVorher
          ? "border-[var(--zustand-fehler)]"
          : "border-[var(--zustand-erfolg)]",
      )}
    >
      <div className="px-3 pt-3">
        <Mikrolabel
          ton={istVorher ? "mittel" : "laut"}
          betont
          className={
            istVorher
              ? "text-[var(--zustand-fehler)]"
              : "text-[var(--zustand-erfolg)]"
          }
        >
          {titel}
        </Mikrolabel>
      </div>
      <pre className="max-h-80 max-w-full overflow-auto p-3 font-mono text-[11.5px] leading-[1.55] text-tinte2">
        <code>{inhalt || "(leer)"}</code>
      </pre>
    </div>
  );
}

function DateiAenderung({
  aenderung,
  standardOffen,
}: {
  aenderung: ArbeitspaketAenderung;
  standardOffen: boolean;
}) {
  return (
    <details className="group min-w-0 border-b border-linie" open={standardOffen}>
      <summary
        className={cx(
          "flex cursor-pointer list-none items-center gap-3 py-3.5 outline-none [&::-webkit-details-marker]:hidden",
          focusRing,
        )}
      >
        <code className="min-w-0 flex-1 break-all font-mono text-[12px] text-tinte">
          {aenderung.pfad}
        </code>
        <Mikrolabel ton="mittel">
          {aenderung.art === "anlegen" ? "NEU" : "ÄNDERN"}
        </Mikrolabel>
        <ChevronDown
          className="size-4 flex-none text-tinte4 transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className="min-w-0 space-y-3 bg-wisch p-3 sm:p-4">
        {aenderung.art === "anlegen" ? (
          <CodeAusschnitt
            titel="Neuer Dateiinhalt"
            inhalt={aenderung.inhalt}
            art="neu"
          />
        ) : (
          aenderung.ersetzungen.map((ersetzung, index) => (
            <div
              key={`${aenderung.pfad}-ersetzung-${index}`}
              className="min-w-0 space-y-2"
            >
              {aenderung.ersetzungen.length > 1 ? (
                <Mikrolabel zahl>
                  Änderung {index + 1} von {aenderung.ersetzungen.length}
                </Mikrolabel>
              ) : null}
              <div className="grid min-w-0 gap-2 lg:grid-cols-2">
                <CodeAusschnitt
                  titel="Vorher · wird entfernt"
                  inhalt={ersetzung.vorher}
                  art="vorher"
                />
                <CodeAusschnitt
                  titel="Nachher · wird eingesetzt"
                  inhalt={ersetzung.nachher}
                  art="nachher"
                />
              </div>
            </div>
          ))
        )}
      </div>
    </details>
  );
}

export function ArbeitspaketPatchVorschau({
  aenderungen,
}: {
  aenderungen: ArbeitspaketAenderung[];
}) {
  return (
    <div className="min-w-0" data-testid="metarr-arbeitspaket-patchvorschau">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-tinte pb-3">
        <Mikrolabel grad={12.5} ton="laut" betont>
          Patch vor der Freigabe
        </Mikrolabel>
        <Mikrolabel zahl>{aenderungen.length} Dateien</Mikrolabel>
      </div>
      <p className="py-3 text-[13px] leading-[1.55] text-tinte3">
        Prüfe jede Datei und den tatsächlichen Inhalt. Erst danach kann der
        eingefrorene Stand nach GitLab geschrieben werden.
      </p>
      <div>
        {aenderungen.map((aenderung, index) => (
          <DateiAenderung
            key={`${aenderung.art}-${aenderung.pfad}-${index}`}
            aenderung={aenderung}
            standardOffen={index === 0}
          />
        ))}
      </div>
    </div>
  );
}
